import { RECEIPT_WIDTH_DOTS, packMonochrome, rasterJob } from './escpos'
import type { SlipLine, SlipSize } from './slipLayout'

/**
 * Draws slip lines on a canvas 576 dots wide (72 mm at 203 dpi) and turns the
 * picture into an ESC/POS print job. Black on white, no anti-aliasing worries:
 * the threshold in packMonochrome decides each dot.
 */

const FONT_FAMILY = '"IBM Plex Sans", "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
const FONT_PX: Record<SlipSize, number> = { sm: 20, md: 26, lg: 32, xl: 52 }
const MARGIN = 8
const INDENT_PX = 28
const LINE_GAP = 6
const GAP_PX = 12
const RULE_PX = 22

function font(size: SlipSize, bold: boolean): string {
  return `${bold ? '700' : '400'} ${FONT_PX[size]}px ${FONT_FAMILY}`
}

/** Splits `text` into lines no wider than `maxWidth`, keeping its own line breaks. */
export function wrapText(
  text: string,
  maxWidth: number,
  measure: (s: string) => number,
): string[] {
  const out: string[] = []
  for (const paragraph of text.split(/\r?\n/)) {
    const words = paragraph.split(/\s+/).filter(Boolean)
    if (words.length === 0) {
      out.push('')
      continue
    }
    let line = ''
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word
      if (measure(candidate) <= maxWidth) {
        line = candidate
        continue
      }
      if (line) out.push(line)
      // A single word wider than the paper is cut by characters.
      let rest = word
      while (measure(rest) > maxWidth && rest.length > 1) {
        let cut = rest.length - 1
        while (cut > 1 && measure(rest.slice(0, cut)) > maxWidth) cut -= 1
        out.push(rest.slice(0, cut))
        rest = rest.slice(cut)
      }
      line = rest
    }
    out.push(line)
  }
  return out
}

interface DrawOp {
  draw: (ctx: CanvasRenderingContext2D, top: number) => void
  height: number
}

function layout(ctx: CanvasRenderingContext2D, lines: SlipLine[], width: number): DrawOp[] {
  const ops: DrawOp[] = []
  const inner = width - MARGIN * 2
  for (const line of lines) {
    if (line.kind === 'gap') {
      ops.push({ height: GAP_PX, draw: () => {} })
    } else if (line.kind === 'rule') {
      ops.push({
        height: RULE_PX,
        draw: (c, top) => {
          c.fillRect(MARGIN, top + RULE_PX / 2 - 1, inner, 3)
        },
      })
    } else if (line.kind === 'label') {
      const f = font('sm', true)
      const px = FONT_PX.sm
      ops.push({
        height: px + LINE_GAP,
        draw: (c, top) => {
          c.font = f
          c.textAlign = 'left'
          c.fillText(line.text.toUpperCase(), MARGIN, top + px)
        },
      })
    } else if (line.kind === 'row') {
      const size = line.size ?? 'md'
      const f = font(size, Boolean(line.bold))
      const px = FONT_PX[size]
      ctx.font = f
      const rightWidth = ctx.measureText(line.right).width
      const wrapped = wrapText(line.left, inner - rightWidth - 16, (s) => ctx.measureText(s).width)
      wrapped.forEach((part, i) => {
        ops.push({
          height: px + LINE_GAP,
          draw: (c, top) => {
            c.font = f
            c.textAlign = 'left'
            c.fillText(part, MARGIN, top + px)
            if (i === 0) {
              c.textAlign = 'right'
              c.fillText(line.right, width - MARGIN, top + px)
            }
          },
        })
      })
    } else {
      const size = line.size ?? 'md'
      const f = font(size, Boolean(line.bold))
      const px = FONT_PX[size]
      const left = MARGIN + (line.indent ?? 0) * INDENT_PX
      ctx.font = f
      const wrapped = wrapText(line.text, width - MARGIN - left, (s) => ctx.measureText(s).width)
      for (const part of wrapped) {
        ops.push({
          height: px + LINE_GAP,
          draw: (c, top) => {
            c.font = f
            if (line.align === 'center') {
              c.textAlign = 'center'
              c.fillText(part, width / 2, top + px)
            } else {
              c.textAlign = 'left'
              c.fillText(part, left, top + px)
            }
          },
        })
      }
    }
  }
  return ops
}

/** The slip as a canvas, for the print job and for an on-screen preview. */
export function renderSlipCanvas(lines: SlipLine[], width = RECEIPT_WIDTH_DOTS): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('This browser cannot draw the slip (no canvas)')
  const ops = layout(ctx, lines, width)
  const height = ops.reduce((sum, op) => sum + op.height, 0) + MARGIN * 2
  canvas.width = width
  canvas.height = height
  // Resizing a canvas resets its state: set everything after it.
  const draw = canvas.getContext('2d')!
  draw.fillStyle = '#fff'
  draw.fillRect(0, 0, width, height)
  draw.fillStyle = '#000'
  draw.textBaseline = 'alphabetic'
  let top = MARGIN
  for (const op of ops) {
    op.draw(draw, top)
    top += op.height
  }
  return canvas
}

/** The slip as ESC/POS bytes: the picture, then feed and cut. */
export function renderSlipJob(lines: SlipLine[]): Uint8Array {
  const canvas = renderSlipCanvas(lines)
  const ctx = canvas.getContext('2d')!
  const image = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const bits = packMonochrome(image.data, canvas.width, canvas.height)
  return rasterJob(bits, canvas.width, canvas.height)
}
